import { Injectable } from '@angular/core'
import { PlatformService } from '../platforms/platform.service'
import { LogService } from 'htv-lib'
import { RouterPage, RouterService } from './router.service'
import { BehaviorSubject, Subscription } from 'rxjs'

/**
 * TV - The input source from TV
 * COMPOSITE - The input source from Composite video (1 channel)
 * SVIDEO - S-Video(Super-Video) and Y/C (2 channels)
 * COMPONENT - The input source from Component video, three cables, each with RCA plugs (3 or more channels)
 * RGB_DVI - The input source from DVI(Digital Visual Interface)
 * HDMI - The input source from HDMI(High-Definition Multimedia Interface)
 * SCART - The input source from SCART(21-pin connector)
 * OTHERS
 */
export enum ExternalInputType {
  TV = 'TV',
  COMPOSITE = 'COMPOSITE',
  SVIDEO = 'SVIDEO',
  COMPONENT = 'COMPONENT',
  RGB_DVI = 'RGB_DVI',
  HDMI = 'HDMI',
  SCART = 'SCART',
  OTHERS = 'OTHERS',
}

export interface ExternalInputData {
  id: string
  type: ExternalInputType
  index: number
  number: number
  connected: boolean
}

@Injectable({
  providedIn: 'root',
})
export class ExternalInputService {
  externalInputs: ExternalInputData[] = []
  externalInputSubject = new BehaviorSubject<ExternalInputData[]>([])
  externalInputSubscription?: Subscription

  constructor(
    private logService: LogService,
    private platformService: PlatformService,
    private routerService: RouterService
  ) {
    // do nothing
  }

  async init(): Promise<void> {
    this.logService.info(`[ExternalInputService] Init`)

    await this.load()
    this.registerExternalInputListener()
  }

  async load(): Promise<void> {
    this.logService.info(`[ExternalInputService] Loading external inputs`)

    this.externalInputs = await this.platformService.getExternalInputs()
    this.logService.log(`[ExternalInputService] Loaded ${this.externalInputs.length} input`)
    this.externalInputs.forEach(externalInput => {
      this.logService.log(`[ExternalInputService] ${externalInput.id} connected=${externalInput.connected}`)
    })
  }

  registerExternalInputListener(): void {
    if (this.externalInputSubscription) {
      this.externalInputSubscription?.unsubscribe()
    }
    this.externalInputSubscription = this.externalInputSubject.subscribe((externalInputs: ExternalInputData[]) => {
      externalInputs.forEach(updatedExternalInput => {
        const externalInput = this.externalInputs.find(ei => ei.id == updatedExternalInput.id)
        if (externalInput) {
          if (externalInput.connected != updatedExternalInput.connected) {
            this.logService.log(
              `[ExternalInputService] ${externalInput.id} connected=${updatedExternalInput.connected}`
            )
          }
          externalInput.connected = updatedExternalInput.connected
        }
      })
    })
    this.platformService.registerExternalInputListener(this.externalInputSubject)
  }

  isHDMIAvailable(portNumber: number | undefined): boolean {
    if (portNumber) {
      return this.externalInputs.some(
        ei => ei.type == ExternalInputType.HDMI && ei.number == portNumber && ei.connected
      )
    } else {
      this.logService.error(`[ExternalInputService] Invalid HDMI port number: "${portNumber}"`)
      return false
    }
  }

  async showHDMI(portNumber: number | undefined): Promise<void> {
    if (portNumber) {
      this.logService.log(`[ExternalInputService] Starting HDMI on port ${portNumber}`)
      const externalInput = this.externalInputs.find(ei => ei.type == ExternalInputType.HDMI && ei.number == portNumber)
      if (externalInput && externalInput.connected) {
        await this.routerService.goToPage(RouterPage.HDMI)
        await this.platformService.showHDMI(externalInput)
      } else {
        await this.routerService.goToPage(RouterPage.HDMI_NOT_FOUND, {
          queryParams: { hdmiPort: portNumber },
        })
        this.logService.error(`[ExternalInputService] Could not find HDMI external input for port ${portNumber}`)
      }
    } else {
      this.logService.error(`[ExternalInputService] Invalid HDMI port number: "${portNumber}"`)
    }
  }

  hideHDMI(): Promise<void> {
    return this.platformService.hideHDMI()
  }
}
